import { useEffect, useState } from 'react'
import { fetchPlayerSeasonData, type PlayerSeasonData } from '@/lib/npbApi'
import type { ApiError } from './useRoster'

interface UsePlayerSeasonDataResult {
  readonly data: PlayerSeasonData | null
  readonly loading: boolean
  readonly error: ApiError | null
}

/**
 * 選手を選んだときに、シーズン成績（Issue #7 API）を取得するフック。
 * `playerId` が null の間は何も取得しない（未選択状態）。
 * 取得中はローディング、失敗時はエラー内容を返す（画面は壊さない。呼び出し側が表示を決める）。
 */
export function usePlayerSeasonData(playerId: string | null): UsePlayerSeasonDataResult {
  const [data, setData] = useState<PlayerSeasonData | null>(null)
  const [loading, setLoading] = useState(playerId !== null)
  const [error, setError] = useState<ApiError | null>(null)

  useEffect(() => {
    if (playerId === null) {
      setData(null)
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setData(null)
    setLoading(true)
    setError(null)

    fetchPlayerSeasonData(playerId)
      .then((result) => {
        if (cancelled) return
        if (result.ok) {
          setData(result.data)
          setError(null)
        } else {
          setData(null)
          setError({ message: result.error, reason: result.reason })
        }
        setLoading(false)
      })
      .catch(() => {
        // fetchPlayerSeasonData 自体は例外を投げない設計だが、念のための保険（画面を壊さない）
        if (cancelled) return
        setData(null)
        setError({ message: '成績の取得に失敗しました。', reason: 'unexpected_error' })
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [playerId])

  return { data, loading, error }
}
